import { type JSX, useCallback, useEffect, useState } from 'react';

import { offeneTeilnehmer as ladeOffene, type Offenerzeile } from '../api/buchungen';
import { ApiFehler } from '../api/fehler';
import { markenklasse } from '../marken';
import { Liste } from '../komponenten/Liste';
import { Meldung } from '../komponenten/Meldung';
import { Segmente } from '../komponenten/Segmente';
import { useZaehler, zaehlwort } from '../komponenten/Seitenkontext';

/**
 * Die Arbeitsliste offener Teilnehmernamen (BUC-70).
 *
 * ============================================================================
 * WARUM SIE EINE EIGENE MASKE IST
 * ============================================================================
 *
 * BUC-70: „Fehlende Teilnehmerdaten werden vor Fristablauf erinnert und ALS
 * ARBEITSLISTE GEFÜHRT; der Termin bleibt trotzdem planbar."
 *
 * Sie gab es schon — als Block unter der gesuchten Buchung, in einer Maske über
 * eine EINZELNE Buchung. Wer morgens fragt, was heute zu tun ist, hat aber
 * keine Buchungsnummer im Kopf: Er hat eine Frist. Die Liste war damit das
 * Ergebnis einer Suche, die man vorher machen musste, um sie zu sehen.
 *
 * Jetzt ist sie der Einstieg, und der Weg läuft in die andere Richtung: von der
 * Frist zur Buchung.
 *
 * ============================================================================
 * DER VORLAUF IST EINE FRAGE UND KEINE EINSTELLUNG
 * ============================================================================
 *
 * „Was läuft in den nächsten dreißig Tagen ab" und „was ist schon abgelaufen"
 * sind zwei verschiedene Arbeiten. Der Vorlauf steht deshalb als Schalter da und
 * nicht in einer Konfiguration — wer am Monatsende aufräumt, stellt ihn anders
 * als wer die Woche plant.
 *
 * Die Vorgabe ist dreißig Tage. Sie ist bewusst großzügig: Eine Liste, die zu
 * kurz greift, sieht leer und erledigt aus.
 *
 * ============================================================================
 * SORTIERT WIRD SERVERSEITIG, NACH DER FRIST
 * ============================================================================
 *
 * Was zuerst abläuft, steht oben — und zwar in derselben Reihenfolge, in der
 * die Erinnerung nach BUC-70 hinausgeht. Eine zweite Sortierung hier würde die
 * Liste anders zeigen, als das System sie abarbeitet.
 *
 * ============================================================================
 * SEITENTYP LISTE (ÄA-41)
 * ============================================================================
 *
 * Der Zähler steht im Seitenkopf, der Vorlauf als Segmente in der
 * Filterleiste, die Summenzeile unter ihr. Eine abgelaufene Meldefrist trägt
 * die Achtungsmarke: Sie ist eine Frist, an der etwas zu tun ist, kein Ende
 * des Vorgangs — der Termin bleibt planbar.
 */

/** Die Vorlaufstufen, zwischen denen die Maske umschaltet. */
const STUFEN: readonly { readonly tage: number; readonly wort: string }[] = [
  { tage: 7, wort: 'nächste Woche' },
  { tage: 30, wort: 'nächster Monat' },
  { tage: 365, wort: 'alle offenen' },
];

function datum(wert: string): string {
  return new Date(wert).toLocaleDateString('de-DE');
}

export function Arbeitsliste({
  beiBuchung,
}: {
  /**
   * Der Weg in die Buchung — die Maske kennt die Navigation nicht selbst.
   *
   * Sie gibt die Buchungsnummer weiter und nicht die Kennung: Die Nummer steht
   * auf dem Beleg des Kunden, und die Buchungsmaske sucht über sie (BUC-51).
   */
  readonly beiBuchung: (buchungsnummer: string) => void;
}): JSX.Element {
  const [zeilen, setzeZeilen] = useState<readonly Offenerzeile[] | null>(null);
  const [vorlauf, setzeVorlauf] = useState(30);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [laedt, setzeLaedt] = useState(true);

  const holen = useCallback(async () => {
    setzeLaedt(true);
    try {
      setzeZeilen(await ladeOffene(vorlauf));
      setzeFehler(null);
    } catch (f) {
      setzeZeilen([]);
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaedt(false);
    }
  }, [vorlauf]);

  useEffect(() => {
    void holen();
  }, [holen]);

  /* ÄA-41: der Zähler im Seitenkopf — erst, wenn geladen ist. */
  useZaehler(laedt || zeilen === null ? undefined : zaehlwort(zeilen.length, 'Buchung', 'Buchungen'));

  /*
   * ABGELAUFEN ZUERST ZÄHLEN, DANN ANZEIGEN.
   *
   * Die Zahl steht über der Liste, weil sie die Frage beantwortet, mit der man
   * herkommt: „Ist etwas liegen geblieben?" Wer sie erst aus der Liste ablesen
   * muss, liest neunzig Zeilen.
   */
  const abgelaufen = (zeilen ?? []).filter((z) => z.fristAbgelaufen).length;
  const namen = (zeilen ?? []).reduce((summe, z) => summe + z.offeneNamen, 0);

  return (
    <>
      {fehler !== null && <Meldung fehler={fehler} />}

      <Liste
        filter={
          <>
            <Segmente
              beschriftung="Vorlauf"
              gewaehlt={String(vorlauf)}
              beiWahl={(wert) => {
                setzeVorlauf(Number(wert));
              }}
              optionen={STUFEN.map((s) => ({ wert: String(s.tage), text: s.wort }))}
            />
            <button
              type="button"
              className="knopf knopf--klein"
              disabled={laedt}
              onClick={() => void holen()}
            >
              Aktualisieren
            </button>
          </>
        }
        hinweise={
          <>
            {/* BUC-70: Der Termin bleibt planbar, auch ohne Namen. */}
            <p className="hilfe">
              Buchungen, bei denen Teilnehmernamen fehlen — nach Meldefrist sortiert, das
              Dringendste oben. Der Termin bleibt planbar, auch wenn die Namen noch nicht da
              sind.
            </p>
            {zeilen !== null && zeilen.length > 0 && (
              <p className="hinweiszeile" role="status">
                {zeilen.length} Buchungen, {namen} fehlende Namen
                {abgelaufen > 0 && (
                  <>
                    {' — '}
                    <strong>{abgelaufen} mit abgelaufener Frist</strong>
                  </>
                )}
                .
              </p>
            )}
          </>
        }
      >
        {laedt && zeilen === null && <p role="status">Wird geladen …</p>}

        {zeilen !== null && zeilen.length === 0 && (
          <p className="liste__leer">
            Keine Buchung wartet auf Teilnehmernamen. Für den gewählten Vorlauf ist alles
            gemeldet.
          </p>
        )}

        {zeilen !== null && zeilen.length > 0 && (
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle" aria-label="Buchungen mit fehlenden Teilnehmernamen">
              <thead>
                <tr>
                  <th scope="col">Buchung</th>
                  <th scope="col">Kunde</th>
                  <th scope="col">Termin</th>
                  <th scope="col">Meldefrist</th>
                  <th scope="col" className="zahl">
                    Offen
                  </th>
                </tr>
              </thead>
              <tbody>
                {zeilen.map((z) => (
                  <tr key={z.buchung} className={z.fristAbgelaufen ? undefined : 'liste__zeile--blass'}>
                    <th scope="row">
                      {/*
                        Der Weg in die Buchung. Er ändert nichts, er führt dorthin,
                        wo man die Namen nachträgt (BUC-69).
                      */}
                      <button
                        type="button"
                        className="liste__oeffnen liste__nummer"
                        onClick={() => {
                          beiBuchung(z.buchungsnummer);
                        }}
                      >
                        {z.buchungsnummer}
                      </button>
                    </th>
                    <td className="liste__name">{z.kundenname}</td>
                    <td>
                      <span className="liste__einzeilig">{datum(z.terminbeginn)}</span>
                      <br />
                      <span className="hilfe">{z.artikel}</span>
                    </td>
                    <td>
                      {z.teilnehmerFrist === null ? (
                        <span className={markenklasse('stillgelegt')}>keine Frist</span>
                      ) : (
                        <>
                          {datum(z.teilnehmerFrist)}{' '}
                          {/* BUC-70: die abgelaufene Frist in Achtung. */}
                          {z.fristAbgelaufen && (
                            <span className={markenklasse('aufmerksamkeit')}>abgelaufen</span>
                          )}
                        </>
                      )}
                    </td>
                    <td className="zahl">
                      <span className={markenklasse(z.fristAbgelaufen ? 'aufmerksamkeit' : 'laufend')}>
                        {z.offeneNamen} von {z.plaetze}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Liste>
    </>
  );
}
